import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import type { APITokenResponse } from '../../../../../shared/settings/apiTokens';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import ActionMenuDropdown from './ActionMenuDropdown';

type SortingRule = {
  id: string;
  desc: boolean;
};

type APITokenTableProps = {
  tokens: APITokenResponse[];
  isLoading: boolean;
  onDeleteToken: (id: number) => void;
  onEditToken: (tokenId: number) => void;
};

const APITokenTable: VoidFunctionComponent<APITokenTableProps> = ({
  tokens,
  isLoading,
  onDeleteToken,
  onEditToken,
}) => {
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'name', desc: false });

  const onSetTokenToEdit = (id: number) => {
    const nextToken = tokens.find(t => t.id === id);
    if (nextToken) {
      onEditToken(nextToken.id);
    }
  };

  const columns = [
    {
      id: 'name',
      Header: (
        <TableHeader title="Name" sort={{ id: 'name', currentMode: sortMode }} />
      ),
      accessor: 'name',
      style: {
        textOverflow: 'ellipsis',
      },
      resizable: false,
    },
    {
      id: 'user',
      Header: (
        <TableHeader title="User" sort={{ id: 'user', currentMode: sortMode }} />
      ),
      accessor: ({ email, username }: APITokenResponse) => email || username || '',
      style: {
        textOverflow: 'ellipsis',
      },
      resizable: false,
    },
    {
      id: 'type',
      Header: (
        <TableHeader title="Type" sort={{ id: 'type', currentMode: sortMode }} />
      ),
      accessor: ({ isPushOnly }: APITokenResponse) => isPushOnly ? 'push-only' : 'full',
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: APITokenResponse }) => <span>{original.isPushOnly ? 'push-only' : 'full'}</span>,
      width: 140,
      resizable: false,
    },
    {
      id: 'actions',
      Header: <TableHeader title="Actions" />,
      resizable: false,
      width: 100,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: APITokenResponse }) => (
        <ActionMenuDropdown
          tokenId={original.id}
          onSetTokenToEdit={onSetTokenToEdit}
          onDeleteToken={onDeleteToken}
        />
      ),
    },
  ];

  return (
    <SimpleReactTable
      data={tokens}
      columns={columns}
      defaultSortMode={[{
        id: 'name',
        desc: false,
      }]}
      onSortedChanged={setSortMode}
      showPagination
      cacheId="apiTokens"
      isLoading={isLoading}
    />
  );
};

export default APITokenTable;
